/*
Creates a debounced function that delays invoking func until after wait milliseconds 
have elapsed since the last time the debounced function was invoked. 

The debounced function comes with a cancel method to cancel delayed func invocations 
and a flush method to immediately invoke them. Provide options to indicate whether 
func should be invoked on the leading and/or trailing edge of the wait timeout. 

The func is invoked with the last arguments provided to the debounced function. 
Subsequent calls to the debounced function return the result of the last func 
invocation.

unc (Function): The function to debounce.
[wait=0] (number): The number of milliseconds to delay.
[options={}] (Object): The options object.
[options.leading=false] (boolean): Specify invoking on the leading edge of the timeout.
[options.trailing=true] (boolean): Specify invoking on the trailing edge of the timeout.
*/

export default function debounce<T extends (...args: never[]) => unknown>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = {},
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let lastArgs: Parameters<T> | undefined;
  let lastThis: unknown;
  let result: ReturnType<T> | undefined;
  let lastCallTime: number | undefined;

  const leading = options.leading === undefined ? false : options.leading;
  const trailing = options.trailing === undefined ? true : options.trailing;

  const shouldInvoke = (time: number) => {
    const timeSinceLastCall = time - (lastCallTime || 0);
    return lastCallTime === undefined || timeSinceLastCall >= wait;
  };

  const invokeFunction = (context: unknown, args: Parameters<T>) => {
    result = func.apply(context, args) as ReturnType<T>;
    lastCallTime = Date.now();
    timeoutId = undefined;
    return result;
  };

  const debounced = function (this: unknown, ...args: Parameters<T>) {
    const currentTime = Date.now();
    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;

    const invokeImmediately = leading && shouldInvoke(currentTime);

    if (!timeoutId) {
      if (invokeImmediately) {
        return invokeFunction(lastThis, lastArgs);
      }
    }

    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      if (trailing !== false && lastArgs) {
        invokeFunction(lastThis, lastArgs);
      }
      timeoutId = undefined;
    }, wait);

    return result;
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = undefined;
    lastCallTime = undefined;
  };

  return debounced;
}
